"use client";

import { useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "@/lib/motion";
import { Check, Minus, Plus, Trash2 } from "@/components/icons";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/posthog-client";
import { MAX_SESSIONS, SESSION_BUILDER_COPY, buildSession, seedSessions } from "./data";
import type { ProductVariant, SessionRow } from "./types";

interface Props {
  variant: ProductVariant;
}

export default function SessionBuilder({ variant }: Props) {
  const prefersReducedMotion = useReducedMotion();
  const copy = SESSION_BUILDER_COPY[variant];

  const [selected, setSelected] = useState<string[]>([copy.locations[0].id, copy.locations[1].id]);
  const [count, setCount] = useState(2);
  const [sessions, setSessions] = useState<SessionRow[]>(() => seedSessions(variant));
  const serialRef = useRef(104);

  const capacityLeft = MAX_SESSIONS - sessions.length;
  const canCreate = selected.length > 0 && capacityLeft > 0;

  function toggleLocation(id: string) {
    setSelected((prev) => {
      if (prev.includes(id)) {
        return prev.length > 1 ? prev.filter((l) => l !== id) : prev;
      }
      return [...prev, id];
    });
  }

  function handleCreate() {
    if (!canCreate) return;
    const batch: SessionRow[] = [];
    for (const locationId of selected) {
      for (let i = 0; i < count; i++) {
        if (batch.length >= capacityLeft) break;
        batch.push(buildSession(locationId, copy, serialRef.current));
        serialRef.current += 1;
      }
    }
    setSessions((prev) => [...prev, ...batch]);
    trackEvent("session_builder_batch_created", {
      product: variant,
      locations: selected.length,
      per_location: count,
      created: batch.length,
    });
  }

  function handleDelete(id: string) {
    setSessions((prev) => prev.filter((s) => s.id !== id));
    trackEvent("session_builder_session_deleted", { product: variant });
  }

  function handleClear() {
    setSessions([]);
    trackEvent("session_builder_cleared", { product: variant });
  }

  return (
    <div className="border-border bg-card flex h-[480px] flex-col overflow-hidden rounded-2xl border sm:h-[500px]">
      {/* Window chrome */}
      <div className="border-border bg-foreground/[0.02] flex shrink-0 items-center gap-2 border-b px-4 py-3">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="bg-foreground/15 size-2.5 rounded-full" />
          <span className="bg-foreground/15 size-2.5 rounded-full" />
          <span className="bg-foreground/15 size-2.5 rounded-full" />
        </span>
        <span className="text-muted-foreground ml-2 truncate font-mono text-[11px]">
          app.proxidize.com — Session Builder
        </span>
      </div>

      {/* Builder controls */}
      <div className="border-border shrink-0 space-y-3 border-b px-4 py-3">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Choose session locations">
          {copy.locations.map((location) => {
            const active = selected.includes(location.id);
            return (
              <button
                key={location.id}
                type="button"
                onClick={() => toggleLocation(location.id)}
                aria-pressed={active}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium whitespace-nowrap transition-colors",
                  active
                    ? "text-foreground bg-foreground/[0.06]"
                    : "border-border text-muted-foreground hover:text-foreground/80 hover:bg-foreground/[0.04]"
                )}
                style={
                  active
                    ? { borderColor: `color-mix(in srgb, var(--brand-green) 40%, transparent)` }
                    : undefined
                }
              >
                <span aria-hidden="true">{location.flag}</span>
                {location.label}
                {active && (
                  <Check size={11} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="border-border flex items-center rounded-lg border">
              <button
                type="button"
                onClick={() => setCount((c) => Math.max(1, c - 1))}
                aria-label="Fewer sessions per location"
                className="text-muted-foreground hover:text-foreground px-2 py-1.5 transition-colors"
              >
                <Minus size={12} aria-hidden="true" />
              </button>
              <span className="text-foreground min-w-6 text-center font-mono text-xs font-bold">
                {count}
              </span>
              <button
                type="button"
                onClick={() => setCount((c) => Math.min(4, c + 1))}
                aria-label="More sessions per location"
                className="text-muted-foreground hover:text-foreground px-2 py-1.5 transition-colors"
              >
                <Plus size={12} aria-hidden="true" />
              </button>
            </div>
            <span className="text-muted-foreground text-[11px]">per location</span>
          </div>

          <button
            type="button"
            onClick={handleCreate}
            disabled={!canCreate}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            style={{ backgroundColor: "var(--brand-green)", color: "var(--background)" }}
          >
            <Plus size={13} aria-hidden="true" />
            Create {Math.min(selected.length * count, Math.max(capacityLeft, 0))} sessions
          </button>
        </div>
      </div>

      {/* Session list */}
      <ul className="toc-scroll min-h-0 flex-1 overflow-y-auto px-2 py-2" aria-label="Sessions">
        <AnimatePresence initial={false}>
          {sessions.map((session) => (
            <motion.li
              key={session.id}
              layout={!prefersReducedMotion}
              initial={prefersReducedMotion ? false : { opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? undefined : { opacity: 0, x: 24 }}
              transition={{ duration: 0.22 }}
              className="hover:bg-foreground/[0.03] flex items-center gap-3 rounded-lg px-2 py-2"
            >
              <span className="shrink-0 text-sm" aria-hidden="true">
                {session.flag}
              </span>
              <span className="text-foreground/90 w-20 shrink-0 truncate font-mono text-[11px] font-semibold sm:w-24">
                {session.name}
              </span>
              <span className="text-muted-foreground hidden flex-1 truncate text-xs sm:block">
                {session.locationLabel}
              </span>
              <span className="text-muted-foreground hidden shrink-0 font-mono text-[11px] md:block">
                {session.accessPoint}
              </span>
              <span
                className="flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold"
                style={{
                  color: "var(--brand-green)",
                  borderColor: `color-mix(in srgb, var(--brand-green) 30%, transparent)`,
                  backgroundColor: `color-mix(in srgb, var(--brand-green) 8%, transparent)`,
                }}
              >
                <span
                  className="size-1 rounded-full"
                  style={{ backgroundColor: "var(--brand-green)" }}
                  aria-hidden="true"
                />
                Active
              </span>
              <button
                type="button"
                onClick={() => handleDelete(session.id)}
                aria-label={`Delete session ${session.name}`}
                className="text-muted-foreground hover:bg-foreground/[0.06] light:hover:text-red-600 shrink-0 rounded-md p-1.5 transition-colors hover:text-red-400"
              >
                <Trash2 size={13} aria-hidden="true" />
              </button>
            </motion.li>
          ))}
        </AnimatePresence>
        {sessions.length === 0 && (
          <li className="text-muted-foreground flex h-full items-center justify-center px-6 text-center text-xs">
            No active sessions — pick locations above and create a batch.
          </li>
        )}
      </ul>

      {/* Footer */}
      <div className="border-border flex shrink-0 items-center justify-between border-t px-4 py-2.5">
        <span className="text-muted-foreground text-[11px]">
          <span className="text-foreground font-semibold">{sessions.length}</span> of {MAX_SESSIONS}{" "}
          active sessions
        </span>
        {sessions.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="text-muted-foreground light:hover:text-red-600 -m-2 flex items-center gap-1.5 p-2 text-[11px] font-medium transition-colors hover:text-red-400"
          >
            <Trash2 size={12} aria-hidden="true" />
            Delete all
          </button>
        )}
      </div>
    </div>
  );
}
